import { useState } from "react";
import { MessageCircle, Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function Footer() {
  const [showUnsub, setShowUnsub] = useState(false);
  const [unsubEmail, setUnsubEmail] = useState("");
  const [unsubLoading, setUnsubLoading] = useState(false);

  const submitUnsub = async (e) => {
    e.preventDefault();
    setUnsubLoading(true);
    try {
      await api.post("/unsubscribe", { email: unsubEmail });
      toast.success("You're unsubscribed. We'll miss you!");
      setShowUnsub(false);
      setUnsubEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUnsubLoading(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground py-14" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-primary-foreground/20">
                <img src="/logos/compass_mark.png" alt="TheKhoj compass logo" className="w-full h-full object-cover" />
              </span>
              <span className="font-display text-xl font-extrabold">TheKhoj</span>
            </div>
            <p className="text-sm opacity-80 leading-relaxed">
              Where passion meets profession. A youth career discovery program helping students turn their natural interests into fulfilling, real-world professions.
            </p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] opacity-70 mb-4">Explore</p>
            <div className="space-y-2.5 text-sm">
              <a href="#program" className="block opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-link-program">Career Exploration Program</a>
              <a href="#pricing" className="block opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-link-pricing">Pricing</a>
              <a href="#contact" className="block opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-link-contact">Enquire Now</a>
              <a href="/brochure/TheKhoj-Brochure.pdf" download="TheKhoj-Brochure.pdf" className="block opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-link-brochure">Download Brochure (PDF)</a>
            </div>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] opacity-70 mb-4">Reach Us</p>
            <div className="space-y-3 text-sm">
              <a href="https://wa.me/919035083242" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-whatsapp-link">
                <MessageCircle className="w-4 h-4" /> +91 9035083242
              </a>
              <a href="mailto:shreecarpediem@gmail.com" className="flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity" data-testid="footer-email-link">
                <Mail className="w-4 h-4" /> shreecarpediem@gmail.com
              </a>
            </div>
            <p className="text-xs opacity-60 mt-6">All sessions are virtual & 1-to-1. Serving students across India, Grades 9–12.</p>
          </div>
        </div>
        <div className="border-t border-primary-foreground/15 mt-10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs opacity-60">
          <span>© 2026 TheKhoj. All rights reserved.</span>
          <button onClick={() => setShowUnsub(true)} className="underline underline-offset-2 hover:opacity-100 transition-opacity text-left sm:text-right" data-testid="footer-unsubscribe-link">
            Unsubscribe from TheKhoj
          </button>
        </div>
      </div>

      {showUnsub && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4" data-testid="unsubscribe-modal">
          <div className="w-full max-w-sm bg-card text-foreground border border-border rounded-3xl p-7 shadow-2xl">
            <div className="flex items-start justify-between mb-2">
              <p className="font-display text-lg font-bold">Unsubscribe</p>
              <button onClick={() => setShowUnsub(false)} aria-label="Close" data-testid="unsubscribe-close"
                className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
              Enter your email and we'll stop all TheKhoj communications and services for it.
            </p>
            <form onSubmit={submitUnsub} className="space-y-4">
              <input required type="email" value={unsubEmail} onChange={(e) => setUnsubEmail(e.target.value)}
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Your email address" data-testid="unsubscribe-email-input" />
              <button type="submit" disabled={unsubLoading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                data-testid="unsubscribe-submit-button">
                {unsubLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {unsubLoading ? "Submitting…" : "Unsubscribe Me"}
              </button>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
}
